import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Search, Loader2, ExternalLink, MapPin, Euro, Globe, ShoppingCart,
  Truck, Car, Wrench, Package, Tag, Hammer, Newspaper, Settings, Tractor,
} from 'lucide-react';
import api from '@/lib/api';

const ICONS = {
  'shopping-cart': ShoppingCart, tractor: Tractor, truck: Truck, car: Car,
  wrench: Wrench, package: Package, tag: Tag, hammer: Hammer,
  newspaper: Newspaper, settings: Settings, globe: Globe,
};

export default function RechercheWebPage() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get('/recherche-web/sites').then(res => setSites(res.data?.sites || [])).catch(() => {});
  }, []);

  const toggleSite = (id) => {
    setSelectedSites(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const handleSearch = async () => {
    if (!query.trim()) { toast.error('Tapez une recherche'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: query, limite: '10' });
      if (selectedSites.length > 0) params.set('sites', selectedSites.join(','));
      const res = await api.get(`/recherche-web/chercher?${params}`);
      setResults(res.data);
      if (!res.data?.resultats?.length) toast.info('Aucun résultat trouvé');
    } catch (e) {
      toast.error('Erreur de recherche');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="recherche-web-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="recherche-title">
          <Globe className="w-8 h-8 text-blue-600" />
          Recherche Web
        </h1>
        <p className="text-muted-foreground">Cherchez sur tous les sites : Leboncoin, pièces auto, matériel agricole...</p>
      </div>

      {/* Barre de recherche */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Ex: répareuse foin, pièce tracteur John Deere, sécateur électrique..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-11 h-12 text-base"
            data-testid="search-input"
          />
        </div>
        <Button onClick={handleSearch} disabled={loading} className="h-12 px-8" data-testid="btn-search">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Search className="w-5 h-5 mr-2" />Rechercher</>}
        </Button>
      </div>

      {/* Filtres sites */}
      <div className="flex flex-wrap gap-2">
        {sites.map(site => {
          const Icon = ICONS[site.icon] || Globe;
          const active = selectedSites.includes(site.id);
          return (
            <Badge
              key={site.id}
              variant={active ? 'default' : 'outline'}
              className={`cursor-pointer py-1.5 px-3 text-sm transition-all ${active ? 'bg-blue-600' : 'hover:bg-muted'}`}
              onClick={() => toggleSite(site.id)}
              data-testid={`site-filter-${site.id}`}
            >
              <Icon className="w-3.5 h-3.5 mr-1.5" />{site.nom}
            </Badge>
          );
        })}
        {selectedSites.length > 0 && (
          <Badge variant="outline" className="cursor-pointer text-red-500 hover:bg-red-50" onClick={() => setSelectedSites([])}>
            Tous les sites
          </Badge>
        )}
      </div>

      {/* Résultats */}
      {results && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              <strong>{results.total_resultats}</strong> résultats sur <strong>{results.sites_cherches}</strong> sites
            </p>
            <div className="flex gap-2">
              {results.sites_status?.map((s, i) => (
                <Badge key={i} variant="outline" className={s.status === 'OK' ? 'border-green-300 text-green-700' : s.status === 'NO_RESULTS' ? 'border-amber-300 text-amber-700' : 'border-red-300 text-red-700'}>
                  {s.site}: {s.count}
                </Badge>
              ))}
            </div>
          </div>

          {results.resultats?.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Search className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-lg font-medium">Aucun résultat</p>
                <p className="text-muted-foreground">Essayez d'autres mots-clés ou sélectionnez d'autres sites</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {results.resultats.map((r, i) => (
                <Card key={i} className="hover:shadow-md transition-shadow overflow-hidden" data-testid={`result-${i}`}>
                  <CardContent className="p-0">
                    <div className="flex">
                      {r.image && (
                        <div className="w-32 h-32 flex-shrink-0 bg-muted">
                          <img src={r.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                      )}
                      <div className="p-4 flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-sm line-clamp-2">{r.titre}</h3>
                          {r.lien && (
                            <a href={r.lien} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-blue-500 hover:text-blue-700">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          {r.prix && (
                            <span className="font-bold text-green-700 flex items-center gap-1">
                              <Euro className="w-3.5 h-3.5" />{r.prix}
                            </span>
                          )}
                          <Badge variant="outline" className="text-xs">{r.site}</Badge>
                        </div>
                        {r.localisation && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{r.localisation}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
